import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { ExchangeRate, ExchangeRateType, OrgUnitScope } from '../CurrencyExchangeView';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
interface ExchangeRateFormProps {
  rate: ExchangeRate | null;
  onSave: (rate: ExchangeRate) => void;
  onCancel: () => void;
  locale: string;
}
export function ExchangeRateForm({
  rate,
  onSave,
  onCancel,
  locale
}: ExchangeRateFormProps) {
  const [formData, setFormData] = useState<Partial<ExchangeRate>>({
    id: '',
    fromCurrency: '',
    toCurrency: '',
    rate: 1,
    inverseRate: 1,
    type: 'Spot' as ExchangeRateType,
    scope: 'Global' as OrgUnitScope,
    isActive: true,
    lastUpdated: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Load data if editing an existing rate
  useEffect(() => {
    if (rate) {
      setFormData({
        ...rate
      });
    }
  }, [rate]);
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    // Handle rate input
    if (name === 'rate') {
      const rateValue = parseFloat(value);
      if (!isNaN(rateValue) && rateValue > 0) {
        setFormData(prev => ({
          ...prev,
          rate: rateValue,
          inverseRate: 1 / rateValue
        }));
      }
      return;
    }
    // Handle scope change
    if (name === 'scope') {
      const scope = value as OrgUnitScope;
      setFormData(prev => ({
        ...prev,
        scope,
        ...(scope === 'Global' ? {
          orgUnitId: undefined,
          orgUnitName: undefined,
          orgUnitPath: undefined
        } : {})
      }));
      return;
    }
    // Handle other fields
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  // Handle OrgUnit selection
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: any) => {
    setFormData(prev => ({
      ...prev,
      orgUnitId,
      orgUnitName: orgUnit?.name,
      orgUnitPath: orgUnit?.path
    }));
    // Clear error
    if (errors.orgUnitId) {
      setErrors(prev => ({
        ...prev,
        orgUnitId: ''
      }));
    }
  };
  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fromCurrency) {
      newErrors.fromCurrency = 'From currency is required';
    }
    if (!formData.toCurrency) {
      newErrors.toCurrency = 'To currency is required';
    }
    if (formData.fromCurrency === formData.toCurrency) {
      newErrors.toCurrency = 'To currency must be different from from currency';
    }
    if (!formData.rate || formData.rate <= 0) {
      newErrors.rate = 'Exchange rate must be greater than 0';
    }
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Organisation unit is required when scope is OrgUnit';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    // Calculate inverse rate if not set
    const rateValue = formData.rate || 1;
    const inverseRateValue = formData.inverseRate || 1 / rateValue;
    // Prepare data for saving
    const rateToSave: ExchangeRate = {
      id: formData.id || '',
      fromCurrency: formData.fromCurrency || '',
      toCurrency: formData.toCurrency || '',
      rate: rateValue,
      inverseRate: inverseRateValue,
      type: formData.type as ExchangeRateType,
      orgUnitId: formData.orgUnitId,
      orgUnitName: formData.orgUnitName,
      orgUnitPath: formData.orgUnitPath,
      scope: formData.scope as OrgUnitScope,
      isActive: formData.isActive || false,
      lastUpdated: formData.lastUpdated || new Date().toISOString(),
      updatedBy: 'Current User'
    };
    onSave(rateToSave);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden h-full" data-id="element-9855">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-9856">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9857">
          {rate ? 'Edit Exchange Rate' : 'Add Exchange Rate'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9858">
          <XIcon className="h-5 w-5" data-id="element-9859" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-9860">
        {/* Currency Pair */}
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-9861">
          <div data-id="element-9862">
            <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-9863">
              From Currency <span className="text-red-500" data-id="element-9864">*</span>
            </label>
            <select id="fromCurrency" name="fromCurrency" className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${errors.fromCurrency ? 'border-red-300' : ''}`} value={formData.fromCurrency} onChange={handleChange} required data-id="element-9865">
              <option value="" data-id="element-9866">Select currency</option>
              <option value="USD" data-id="element-9867">USD - US Dollar</option>
              <option value="EUR" data-id="element-9868">EUR - Euro</option>
              <option value="GBP" data-id="element-9869">GBP - British Pound</option>
              <option value="CAD" data-id="element-9870">CAD - Canadian Dollar</option>
              <option value="AUD" data-id="element-9871">AUD - Australian Dollar</option>
              <option value="JPY" data-id="element-9872">JPY - Japanese Yen</option>
              <option value="CNY" data-id="element-9873">CNY - Chinese Yuan</option>
              <option value="INR" data-id="element-9874">INR - Indian Rupee</option>
              <option value="BRL" data-id="element-9875">BRL - Brazilian Real</option>
              <option value="MXN" data-id="element-9876">MXN - Mexican Peso</option>
              <option value="ZAR" data-id="element-9877">ZAR - South African Rand</option>
              <option value="SGD" data-id="element-9878">SGD - Singapore Dollar</option>
            </select>
            {errors.fromCurrency && <p className="mt-1 text-sm text-red-600" data-id="element-9879">{errors.fromCurrency}</p>}
          </div>

          <div data-id="element-9880">
            <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-9881">
              To Currency <span className="text-red-500" data-id="element-9882">*</span>
            </label>
            <select id="toCurrency" name="toCurrency" className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${errors.toCurrency ? 'border-red-300' : ''}`} value={formData.toCurrency} onChange={handleChange} required data-id="element-9883">
              <option value="" data-id="element-9884">Select currency</option>
              <option value="USD" data-id="element-9885">USD - US Dollar</option>
              <option value="EUR" data-id="element-9886">EUR - Euro</option>
              <option value="GBP" data-id="element-9887">GBP - British Pound</option>
              <option value="CAD" data-id="element-9888">CAD - Canadian Dollar</option>
              <option value="AUD" data-id="element-9889">AUD - Australian Dollar</option>
              <option value="JPY" data-id="element-9890">JPY - Japanese Yen</option>
              <option value="CNY" data-id="element-9891">CNY - Chinese Yuan</option>
              <option value="INR" data-id="element-9892">INR - Indian Rupee</option>
              <option value="BRL" data-id="element-9893">BRL - Brazilian Real</option>
              <option value="MXN" data-id="element-9894">MXN - Mexican Peso</option>
              <option value="ZAR" data-id="element-9895">ZAR - South African Rand</option>
              <option value="SGD" data-id="element-9896">SGD - Singapore Dollar</option>
            </select>
            {errors.toCurrency && <p className="mt-1 text-sm text-red-600" data-id="element-9897">{errors.toCurrency}</p>}
          </div>
        </div>

        {/* Rate Value */}
        <div data-id="element-9898">
          <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-9899">
            Exchange Rate <span className="text-red-500" data-id="element-9900">*</span>
          </label>
          <div className="mt-1 relative rounded-md shadow-sm" data-id="element-9901">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9902">
              <span className="text-gray-500 sm:text-sm" data-id="element-9903">
                1 {formData.fromCurrency || '---'} =
              </span>
            </div>
            <input type="number" name="rate" id="rate" step="0.0001" min="0.0001" className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-24 pr-20 sm:text-sm border-gray-300 rounded-md ${errors.rate ? 'border-red-300' : ''}`} placeholder="0.0000" value={formData.rate || ''} onChange={handleChange} required data-id="element-9904" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-9905">
              <span className="text-gray-500 sm:text-sm" data-id="element-9906">
                {formData.toCurrency || '---'}
              </span>
            </div>
          </div>
          {errors.rate && <p className="mt-1 text-sm text-red-600" data-id="element-9907">{errors.rate}</p>}
          {formData.rate && formData.rate > 0 && <p className="mt-2 text-sm text-gray-500" data-id="element-9908">
              Inverse: 1 {formData.toCurrency || '---'} ={' '}
              {(1 / formData.rate).toFixed(4)} {formData.fromCurrency || '---'}
            </p>}
        </div>

        {/* Rate Type */}
        <div data-id="element-9909">
          <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-9910">
            Rate Type <span className="text-red-500" data-id="element-9911">*</span>
          </label>
          <select id="type" name="type" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.type} onChange={handleChange} required data-id="element-9912">
            <option value="Spot" data-id="element-9913">Spot</option>
            <option value="Contract" data-id="element-9914">Contract</option>
            <option value="Corporate" data-id="element-9915">Corporate</option>
            <option value="Bank" data-id="element-9916">Bank</option>
            <option value="Custom" data-id="element-9917">Custom</option>
          </select>
        </div>

        {/* Scope Selection */}
        <div data-id="element-9918">
          <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-9919">
            Scope <span className="text-red-500" data-id="element-9920">*</span>
          </label>
          <select id="scope" name="scope" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.scope} onChange={handleChange} required data-id="element-9921">
            <option value="Global" data-id="element-9922">Global</option>
            <option value="OrgUnit" data-id="element-9923">Organisation Unit</option>
          </select>
          <p className="mt-1 text-xs text-gray-500" data-id="element-9924">
            {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
          </p>
        </div>

        {/* OrgUnit Selector - only show if scope is OrgUnit */}
        {formData.scope === 'OrgUnit' && <div data-id="element-9925">
            <OrgUnitSelector value={formData.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-9926" />
          </div>}

        {/* Active Status */}
        <div className="flex items-center" data-id="element-9927">
          <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={formData.isActive} onChange={handleChange} data-id="element-9928" />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-9929">
            Is Active
          </label>
        </div>

        {/* Form Actions */}
        <div className="pt-5 border-t border-gray-200" data-id="element-9930">
          <div className="flex justify-end space-x-3" data-id="element-9931">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9932">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9933">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-9934" />
              Save Exchange Rate
            </button>
          </div>
        </div>
      </form>
    </div>;
}